import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import {
  Button,
  Input,
  TextField,
  Label,
  Card,
  CardHeader,
  CardFooter,
  Separator
} from 'src/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
const logo = '/logo.svg';
import UsersApi from 'src/api/users';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setAuthState } from 'src/store/user/userSlice';
import { useAuth0 } from '@auth0/auth0-react';

interface ApiErrorWithMessage {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}
export const RequiredFields = () => {
  const { logout } = useAuth0();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
    }
  }, [user]);
  const fetchAndSetUser = async () => {
    const { data } = await UsersApi.fetchCurrentUser();
    dispatch(
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: data.user
      })
    );
  };
  const submitForm = async () => {
    const schema = z.object({
      firstName: z
        .string()
        .min(1, {
          message: 'First name is required.'
        })
        .max(100, {
          message: 'First name is too long.'
        })
        .regex(/^[a-zA-Z\u00C0-\u017F\s'-]+$/, {
          message: 'First name can only contain letters, spaces, hyphens, and apostrophes.'
        }),
      lastName: z
        .string()
        .min(1, {
          message: 'Last name is required.'
        })
        .max(100, {
          message: 'Last name is too long.'
        })
        .regex(/^[a-zA-Z\u00C0-\u017F\s'-]+$/, {
          message: 'Last name can only contain letters, spaces, hyphens, and apostrophes.'
        })
    });
    const result = schema.safeParse({
      firstName,
      lastName
    });
    if (!result.success) {
      const errorMessage = result.error.errors[0].message;
      setError(errorMessage);
      console.warn('Form validation failed:', errorMessage);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await UsersApi.updateUserProfile({
        firstName,
        lastName
      });
      await fetchAndSetUser();
    } catch (err: unknown) {
      console.error('Error updating profile:', err);
      const typedError = err as ApiErrorWithMessage;
      const errorMessage =
        typedError.response?.data?.message ??
        typedError.message ??
        'Failed to update profile. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    if (e && typeof e?.preventDefault === 'function') {
      e.preventDefault();
    }
    await submitForm();
  };
  return (
    <div className="min-h-screen flex flex-col">
      <div className="w-full flex justify-end p-4">
        <Button
          isIconOnly
          variant="ghost"
          onPress={() =>
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/logout'
              }
            })
          }>
          <FontAwesomeIcon icon={faSignOutAlt} />
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center flex flex-col items-center">
            <div className="flex items-center">
              <img src={logo} alt="Logo" className="h-20 w-auto" />
            </div>
            <h1 className="text-2xl font-bold">Almost there!</h1>
            <p className="text-default-500">Please complete your profile to continue.</p>
          </CardHeader>
          <Separator />
          <Card.Content>
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <TextField value={firstName} onChange={setFirstName} isRequired fullWidth>
                <Label>First Name</Label>
                <Input type="text" variant="secondary" />
              </TextField>

              <TextField value={lastName} onChange={setLastName} isRequired fullWidth>
                <Label>Last Name</Label>
                <Input type="text" variant="secondary" />
              </TextField>

              {error &&
                !error.toLowerCase().includes('first') &&
                !error.toLowerCase().includes('last') && (
                  <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                    <p className="text-danger text-sm">{error}</p>
                  </div>
                )}
            </form>
          </Card.Content>
          <Separator />
          <CardFooter>
            <Button variant="primary" className="w-full" isPending={isLoading} onPress={submitForm}>
              {isLoading ? 'Submitting...' : 'Save & Continue'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
